/* THE FIVE SCORES, SHOWING THEIR ARITHMETIC                          (PRD 6)
 *
 * "The scores show their arithmetic: the overall, then each of the five underneath
 * with its inputs."
 *
 * The arithmetic is the point of this panel rather than a detail of it. A score
 * nobody can take apart is a verdict, and a coach handed a verdict about a person
 * they know can only either accept it or ignore it. Every number here opens into
 * the inputs that produced it, and every input says what it read.
 *
 * WHAT AN UNKNOWN INPUT LOOKS LIKE MATTERS MOST. It reads "not recorded" and names
 * what would fill it, because "an input marked no source is shown as unknown, never
 * as zero". A nought would be the product asserting something about the member that
 * nobody has ever checked, which is the single failure this whole model is built to
 * avoid.
 *
 * NUMBERS ARE FOR THE OWNER AND THE MANAGER (PRD 4). The floor gets the order and
 * the reason in plain words, so this panel is not rendered on their seats at all -
 * the caller decides, because it is the caller that knows who is looking.
 */
import type { Score, ScoreResult } from '@acrostic/model';
import { SCORE_AMBER, SCORE_RED } from '@acrostic/model';
import { Panel } from '../../components/Panel.js';

const toneOf = (v: number | null): string =>
  v == null ? 'flat' : v <= SCORE_RED ? 'risk' : v <= SCORE_AMBER ? 'warn' : 'good';

export function Scores({ scores }: { scores: ScoreResult }) {
  return (
    <Panel
      title="What the number is made of"
      eyebrow="Every score counts down from 100, and a low one is the gym's work rather than the member's"
    >
      <div className="scorelist">
        {scores.scores.map((s) => <ScoreRow key={s.key} s={s} />)}
      </div>
    </Panel>
  );
}

function ScoreRow({ s, nested = false }: { s: Score; nested?: boolean }) {
  return (
    <details className={'scorerow' + (nested ? ' nested' : '')}>
      <summary>
        <span className="t-label-large sname">{s.label}</span>
        <span className="t-caption muted squestion">{s.question}</span>
        {/* AN UNANSWERABLE SCORE SAYS SO. It is not a zero and it is not blank:
            both would be claims about the member that nobody has checked. */}
        <span className={'pill score t-label-small ' + toneOf(s.value)}>
          {s.value == null ? 'not recorded' : s.value}
        </span>
      </summary>

      <div className="inputs">
        {s.inputs.map((i) => (
          <div className={'sinput' + (i.value == null ? ' unknown' : '')} key={i.key}>
            <div className="t-body-small ilabel">{i.label}</div>
            {i.value == null ? (
              <div className="t-caption muted ifill">Not recorded. {i.fills}</div>
            ) : (
              <>
                <div className="ibar" aria-hidden="true">
                  <i style={{ width: Math.round(i.value * 100) + '%' }} />
                </div>
                {i.why && <div className="t-caption iwhy">{i.why}</div>}
              </>
            )}
          </div>
        ))}

        {/* PROGRAM PROGRESS IS FIVE PILLARS, so it nests rather than flattening:
            "each pillar shows its own score and its own inputs, so a coach can see
            whether a low reading is real or simply unrecorded". */}
        {s.pillars?.map((p) => <ScoreRow key={p.key} s={p} nested />)}
      </div>
    </details>
  );
}
